import { createContainer } from 'shared-components/unstated'
import { ThemeVariants } from 'shared-definitions/types'
import { useEffect, useState } from 'react'

/** What the reader picked: a theme, or "follow this device" (the account Settings' "System"). */
export type ThemeMode = ThemeVariants | 'system'

export interface ThemeValueProps {
  theme: ThemeVariants
  ready: boolean
}

export interface ThemeContextProps extends ThemeValueProps {
  setTheme: (newState: ThemeVariants) => void
  /** The stored choice; 'system' only once a reader picked "System" (additive: a first visit
   *  still stores the device's theme as before, so the menu toggle behaves as it always did). */
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

function themeModeMatcher(condition: ThemeVariants): boolean {
  return window.matchMedia(`(prefers-color-scheme: ${condition})`).matches
}

function systemTheme(): ThemeVariants {
  return themeModeMatcher('dark') ? 'dark' : 'light'
}

const key = 'site-theme'

function updateKey(value: ThemeMode): void {
  localStorage.setItem(key, value)
}

function getKey(): string | null {
  return localStorage.getItem(key)
}

export const ThemeContext = createContainer<ThemeContextProps, ThemeValueProps>(
  (initial = { theme: 'light', ready: false }) => {
    const [theme, setTheme] = useState<ThemeVariants>(initial.theme)
    const [mode, setModeState] = useState<ThemeMode>(initial.theme)
    const [ready, setReady] = useState(initial.ready)
    const updateFunc = (newVal: ThemeVariants): void => {
      updateKey(newVal)
      setTheme(newVal)
      setModeState(newVal)
      setReady(true)
    }
    const setMode = (next: ThemeMode): void => {
      if (next !== 'system') {
        updateFunc(next)
        return
      }
      updateKey('system')
      setModeState('system')
      setTheme(systemTheme())
      setReady(true)
    }

    useEffect(() => {
      const savedValue = getKey()
      if (savedValue === 'system') {
        setModeState('system')
        setTheme(systemTheme())
        setReady(true)
        return
      }
      if (savedValue) {
        const saved = savedValue === 'dark' ? 'dark' : 'light'
        setTheme(saved)
        setModeState(saved)
        setReady(true)
        return
      }

      updateFunc(systemTheme())
    }, [])

    // "System" follows the device while the page is open, too.
    useEffect(() => {
      if (mode !== 'system' || typeof window.matchMedia !== 'function') {
        return undefined
      }
      const query = window.matchMedia('(prefers-color-scheme: dark)')
      const follow = (): void => setTheme(query.matches ? 'dark' : 'light')
      query.addEventListener?.('change', follow)
      return () => query.removeEventListener?.('change', follow)
    }, [mode])

    return {
      ready,
      theme,
      setTheme: updateFunc,
      mode,
      setMode,
    }
  }
)
